/// <amd-module name="Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshReducer" />
import { default as produce } from "immer";
import { SuitesActions, SuitesActionsTypes } from "../Suites/SuitesActions";
import { TestHubRefreshActions, TestHubRefreshActionsTypes } from "./TestHubRefreshActions";
import { ITestHubRefreshState } from "./TestHubRefreshContracts";
/**
 * TestHubRefreshReducer: Reducer which populates the base data for the page in the state.
 * The base data includes test plan id, tset plan name, root suite id etc. This can be considered as global data that every sub-state would require.
 */
export function TestHubRefreshReducer(state: ITestHubRefreshState, action: SuitesActions | TestHubRefreshActions): ITestHubRefreshState {
    state = state || ({} as ITestHubRefreshState);

    return produce(state, (draftState: ITestHubRefreshState) => {
        switch (action.type) {
            case TestHubRefreshActionsTypes.changeTestCasePreviewStatus:
                if (action.payload && action.payload.previewStatus) {
                    draftState.testCasePreviewStatus = action.payload.previewStatus;
                }
                break;
            case SuitesActionsTypes.loadInitialTestDataSucceeded:
                draftState.testPlanId = action.payload.testPlan.id;
                draftState.rootSuiteId = action.payload.testPlan.rootSuiteId;
                draftState.selectedSuiteId = action.payload.selectedSuiteId;
                draftState.testPlanIteration = action.payload.testPlan.iteration;
                draftState.testPlanAreaPath = action.payload.testPlan.areaPath;
                draftState.testPlanStartDate = action.payload.testPlan.startDate;
                draftState.testPlanEndDate = action.payload.testPlan.endDate;
                draftState.testPlanName = action.payload.testPlan.name;
                draftState.initialSelectedSuiteId = action.payload.selectedSuiteId;
                draftState.isAdvancedExtensionEnabled = action.payload.isAdvancedExtensionEnabled;
                draftState.testCasePageSize = action.payload.testCasePageSize;
                draftState.testPointPageSize = action.payload.testPointPageSize;
                draftState.defineColumnOptionFields = action.payload.defineColumnOptionFields;
                draftState.executeColumnOptionFields = action.payload.executeColumnOptionFields;
                draftState.isShowTestsFromChildSuitesEnabled = false;
                break;

            case SuitesActionsTypes.changeSelectedSuite:
                draftState.selectedSuiteId = action.payload.selectedSuiteId;
                draftState.selectedSuiteName = action.payload.selectedSuiteName;
                draftState.isInGridView = false;
                break;

            case SuitesActionsTypes.updateTestSuiteName:
                if (draftState.selectedSuiteId == action.payload.testSuiteId) {
                    draftState.selectedSuiteName = action.payload.testSuiteTitle;
                }
                break;

            case SuitesActionsTypes.loadTestPlan:
                draftState.testPlanId = action.payload.testPlan.id;
                draftState.rootSuiteId = action.payload.testPlan.rootSuite.id;
                draftState.testPlanIteration = action.payload.testPlan.iteration;
                draftState.testPlanAreaPath = action.payload.testPlan.areaPath;
                draftState.testPlanStartDate = action.payload.testPlan.startDate;
                draftState.testPlanEndDate = action.payload.testPlan.endDate;
                draftState.testPlanName = action.payload.testPlan.name;
                draftState.isShowTestsFromChildSuitesEnabled = false;
                break;

            case SuitesActionsTypes.updateLoadingStatus:
                draftState.isLoading = action.payload.loadingStatus;
                break;

            case SuitesActionsTypes.updateReloadState:
                draftState.isReload = action.payload.state;
                break;

            case TestHubRefreshActionsTypes.updateBusyViewStatus:
                draftState.isBusy = action.payload.isBusy;
                break;

            case TestHubRefreshActionsTypes.updatePagingStatus:
                draftState.isPagingInProgress = action.payload.isPagingInProgress;
                break;

            case TestHubRefreshActionsTypes.updateTestPlan:
                draftState.testPlanName = action.payload.testPlanName;
                draftState.testPlanStartDate = action.payload.testPlanStartDate;
                draftState.testPlanEndDate = action.payload.testPlanFinishDate;
                draftState.testPlanAreaPath = action.payload.testPlanAreaPath;
                draftState.testPlanIteration = action.payload.testPlanIterationPath;

                if (draftState.rootSuiteId == draftState.selectedSuiteId) {
                    draftState.selectedSuiteName = action.payload.testPlanName;
                }
                break;

            case TestHubRefreshActionsTypes.setGridView:
                draftState.isInGridView = action.payload.isInGridView;
                break;

            case TestHubRefreshActionsTypes.setIsGridDirty:
                draftState.isGridDirty = action.payload.isGridDirty;
                break;

            case TestHubRefreshActionsTypes.loadExecuteColumnOptionFieldsSucceeded:
                draftState.executeColumnOptionFields = action.payload.executeColumnOptionFields;
                break;

            case TestHubRefreshActionsTypes.loadDefineColumnOptionFieldsSucceeded:
                draftState.defineColumnOptionFields = action.payload.defineColumnOptionFields;
                break;

            case TestHubRefreshActionsTypes.changeDefineColumnWidths:
                if (action.payload.columnId && action.payload.width) {
                    if (!draftState.defineColumnAndWidthMap) {
                        draftState.defineColumnAndWidthMap = {};
                    }
                    draftState.defineColumnAndWidthMap[action.payload.columnId] = action.payload.width;
                }
                if (action.payload.columnAndWidthObj) {
                    draftState.defineColumnAndWidthMap = action.payload.columnAndWidthObj;
                }
                break;

            case TestHubRefreshActionsTypes.changeExecuteColumnWidths:
                if (action.payload.columnId && action.payload.width) {
                    if (!draftState.executeColumnAndWidthMap) {
                        draftState.executeColumnAndWidthMap = {};
                    }
                    draftState.executeColumnAndWidthMap[action.payload.columnId] = action.payload.width;
                }
                if (action.payload.columnAndWidthObj) {
                    draftState.executeColumnAndWidthMap = action.payload.columnAndWidthObj;
                }
                break;

            case TestHubRefreshActionsTypes.changeSplitterWidth:
                if (action.payload && action.payload.splitterWidth) {
                    draftState.splitterWidth = action.payload.splitterWidth;
                }
                break;

            case TestHubRefreshActionsTypes.updateSelectedBuild:
                draftState.selectedBuildId = action.payload.selectedBuildId;
                draftState.selectedBuildDefnId = action.payload.selectedBuildDefnId;
                break;

            case TestHubRefreshActionsTypes.updateSelectedRelease:
                draftState.selectedReleaseId = action.payload.selectedReleaseId;
                draftState.selectedReleaseEnvironmentId = action.payload.selectedReleaseEnvironmentId;
                break;

            case TestHubRefreshActionsTypes.showTestsFromChildSuitesClick:
                draftState.isShowTestsFromChildSuitesEnabled = action.payload.isShowTestsFromChildSuitesEnabled;
                break;

            case TestHubRefreshActionsTypes.loadMineAllPageUrl:
                draftState.mineAllPageUrl = action.payload.mineAllPageUrl;
                break;

            case TestHubRefreshActionsTypes.updateDefineTabCustomColumnFieldMap:
                draftState.defineTabCustomColumnFieldMap = action.payload.customColumnFieldMap;
                break;

            case TestHubRefreshActionsTypes.updateExecuteTabCustomColumnFieldMap:
                draftState.executeTabCustomColumnFieldMap = action.payload.customColumnFieldMap;
                break;

            default:
                break;
        }
    });
}
